<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header :title="isCompletedView ? 'Completed Enrollments' : 'Current Enrollments'" :count="isCompletedView && enrollments ? completedEnrollments.length || null : null" icon="fa-chalkboard-teacher"/>

            <p class="has-text-white mt-6" v-if="!enrollments"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading your enrollments...</p>

            <!-- Empty states sit outside the list column so they center on the whole panel -->
            <empty-hero v-else-if="!isCompletedView && activeEnrollments.length === 0" icon="fa-chalkboard-teacher" title="No courses in progress"
                        :text="completedEnrollments.length ? 'Start something new from the catalog, or revisit a course you have finished.' : 'Browse the catalog to find a course and get started.'">
                <b-button tag="router-link" :to="{name: 'course-catalog'}" type="is-light"><i class="fad fa-browsers mr-2"></i>Browse the Course Catalog</b-button>
                <b-button v-if="completedEnrollments.length" tag="router-link" :to="{name: 'enrollments', params: {view: 'completed'}}" type="is-light" outlined><i class="fad fa-check-to-slot mr-2"></i>Completed Courses ({{ completedEnrollments.length }})</b-button>
            </empty-hero>
            <empty-hero v-else-if="isCompletedView && completedEnrollments.length === 0 && reactivatedEnrollments.length === 0" icon="fa-check-to-slot" title="No completed courses yet" text="Courses you finish are archived here by certification and year.">
                <b-button v-if="activeEnrollments.length" tag="router-link" :to="{path: '/enrollments'}" type="is-light"><i class="fad fa-circle-play mr-2"></i>Current Enrollments ({{ activeEnrollments.length }})</b-button>
                <b-button tag="router-link" :to="{name: 'course-catalog'}" type="is-light" :outlined="activeEnrollments.length > 0"><i class="fad fa-browsers mr-2"></i>Browse the Course Catalog</b-button>
            </empty-hero>

            <div class="columns mt-6" v-else>
                <div class="column is-list">
                    <!-- Current view (/enrollments) -->
                    <div v-if="!isCompletedView">
                        <div class="columns">
                            <div class="column">
                                <enrollment :enrollment="enroll" v-for="enroll in activeEnrollments" class="mb-5"/>
                            </div>
                        </div>
                    </div>

                    <!-- Completed view (/enrollments/completed) -->
                    <div v-else>
                        <section v-if="completedEnrollments.length !== 0">

                            <!-- One year at a time (tab bar), grouped by certification
                                 inside the year; courses without one trail as "Other" -->
                            <year-tabs :years="completedYears" :value="activeCompletedYear.key" @input="completedYear = $event"/>
                            <empty-state v-if="activeCompletedYear.groups.length === 0" icon="fa-info-circle" :text="'No courses completed in ' + activeCompletedYear.label + '.'"/>
                            <template v-for="group in activeCompletedYear.groups">
                                <section-header v-if="activeCompletedYear.groups.length > 1 || group.key !== 'other'" :key="'gh-' + group.key"
                                                :title="group.title" :icon="group.key === 'other' ? 'fa-check-to-slot' : 'fa-file-certificate'" :count="group.items.length" class="mt-2 mb-4"/>
                                <enrollment v-for="enroll in group.items" :key="enroll.id" :enrollment="enroll" class="mb-5"/>
                            </template>
                        </section>

                        <!-- Enrollments retired via the >365-day-expired reactivation path
                             (a new enrollment now carries the course forward) -- effectively
                             dead records at this point, kept visible only as history -->
                        <section v-if="reactivatedEnrollments.length !== 0" class="pt-4">
                            <section-header class="mt-6" title="Reactivated Courses" icon="fa-rotate" :count="reactivatedEnrollments.length"/>
                            <enrollment :enrollment="enroll" v-for="enroll in reactivatedEnrollments" class="mb-5"/>
                        </section>

                    </div>


                </div>
            </div>

        </template>
    </framework>
</template>

<script>
    import Framework from "../../components/framework/Framework";
    import MainHeader from "../../components/framework/MainHeader";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import Enrollment from "../../components/Enrollment";
    import YearTabs from "../../components/framework/YearTabs";
    import {bucketByYear, defaultYearKey} from "../../utils/yearBuckets";
    import EmptyState from "../../components/framework/EmptyState";
    import SectionHeader from "../../components/framework/SectionHeader";
    import EmptyHero from "../../components/framework/EmptyHero";

    import {mapActions, mapGetters} from "vuex";

    export default {
        components: {YearTabs, SectionHeader, EmptyHero, EmptyState, Enrollment, Framework, SidePanelNav, MainHeader},
        props: {
            // Route param: '' (current) | 'completed'
            view: {type: String, default: ''}
        },
        data: function() {
            return {
                completedYear: null,
                showCompleted: false
            };
        },
        computed: {
            isCompletedView() {
                return this.view === 'completed';
            },
            ...mapGetters(['enrollments', 'currentUser']),
            activeEnrollments: function() {
                let enrollments = [];
                for (let i = 0; i < this.enrollments.length; i++) {
                    const enrollment = this.enrollments[i];
                    if (!enrollment.completed_at && !enrollment.reactivated_to_enrollment_id) enrollments.push(enrollment);
                }

                enrollments.sort((a, b) => {
                    return new Date(b.started_at) - new Date(a.started_at);
                });

                return enrollments;
            },
            // Retired via the >365-day-expired reactivation path -- a new enrollment
            // now carries the course forward, this one is effectively dead
            reactivatedEnrollments: function() {
                let enrollments = [];
                for (let i = 0; i < this.enrollments.length; i++) {
                    const enrollment = this.enrollments[i];
                    if (enrollment.reactivated_to_enrollment_id) enrollments.push(enrollment);
                }

                enrollments.sort((a, b) => {
                    return new Date(b.reactivated_at) - new Date(a.reactivated_at);
                });

                return enrollments;
            },
            completedEnrollments: function() {
                let enrollments = [];
                for (let i = 0; i < this.enrollments.length; i++) {
                    const enrollment = this.enrollments[i];
                    if (enrollment.completed_at && !enrollment.reactivated_to_enrollment_id) enrollments.push(enrollment);
                }

                enrollments.sort((a, b) => {
                    return new Date(b.completed_at) - new Date(a.completed_at);
                });

                return enrollments;
            },
            // Completed enrollments grouped by certification > completion year.
            // Categories were tried here first and did not work: a course carries
            // up to 23 of them (they exist so the catalog can be browsed several
            // ways), so "the" category was really just the first one assigned --
            // every 23-category course filed itself under "Course Development".
            // Certification is a genuine single value per enrollment, and the year
            // is what students actually count renewals in.
            // Null when no completed enrollment has a certification (flat fallback).
            // Completed courses by completion year (newest first); inside a
            // year, by certification with uncertified courses last
            completedYears: function() {
                return bucketByYear(this.completedEnrollments, {
                    date: e => e.completed_at,
                    groupKey: e => (e.certification_id && e.certification_title) ? e.certification_id : 'other',
                    groupTitle: e => (e.certification_id && e.certification_title) ? e.certification_title : 'Other Completed Courses'
                });
            },
            activeCompletedYear: function() {
                const years = this.completedYears;
                return years.find(y => y.key === this.completedYear) || years.find(y => y.key === defaultYearKey(years)) || {groups: [], label: ''};
            },
        },
        methods: {
            ...mapActions(['getEnrollments'])
        },
        mounted() {
            this.getEnrollments();
        }
    };
</script>

<style lang="scss" scoped>
    h2 {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }

</style>

<style lang="scss">

</style>
